import { useFragment } from "@/generated";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { LessonFieldsFragmentDoc, MoveLessonsDocument } from "../../../generated/graphql";

/**
 * Hook for moving lessons to a different parent project or to root level.
 *
 * Provides a mutation function to relocate one or more lessons to a new parent project
 * or to the root level (no parent). Automatically refetches root and parent lesson
 * queries after the move to update the UI with the new lesson locations.
 *
 * @returns An object containing:
 *   - moveLessons: Async function to move lessons. Takes lessonIds array and targetParentId (or null for root).
 *   - loading: Boolean indicating if the mutation is in progress
 *
 * @example
 * const { moveLessons, loading } = useMoveLessons();
 * await moveLessons(["lesson1", "lesson2"], "parentProjectId"); // Move to parent
 * await moveLessons(["lesson1"], null); // Move to root
 */
export function useMoveLessons() {
  const [moveLessonsMutation, { loading }] = useMutation(MoveLessonsDocument, {
    update(cache, { data }, { context, variables }) {
      if (!data?.moveLesson) return;

      try {
        const lessons = data.moveLesson.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        const targetParentId = variables?.input.targetParentId;
        const sourceParentId = context?.sourceParentId;

        // Allow null (root level) as a valid sourceParentId
        if (sourceParentId === undefined) return;

        cache.batch({
          // Add lesson to target project cache
          update(cache) {
            for (const lesson of lessons) {
              const id = cache.identify(lesson);
              if (id) {
                cache.writeFragment({
                  id,
                  fragment: LessonFieldsFragmentDoc,
                  data: lesson,
                });
              }
            }

            const newRefs = lessons
              .map((p) => cache.identify(p))
              .filter(Boolean)
              .map((id) => ({ __ref: id }));

            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                lessonsByParentId(existingData, { storeFieldName }) {
                  const key =
                    targetParentId === null ? `"parentId":null` : `"parentId":"${targetParentId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;
                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: [...newRefs, ...existingData.items],
                    total: (existingData.total || 0) + newRefs.length,
                  };
                },
              },
            });

            if (targetParentId !== null) {
              cache.modify({
                id: `Project:${targetParentId}`,
                fields: {
                  itemCount(existingData = 0) {
                    return existingData + newRefs.length;
                  },
                },
              });
            }

            // Remove lesson from source project cache
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                lessonsByParentId(existingData, { storeFieldName, readField }) {
                  const key =
                    sourceParentId === null ? `"parentId":null` : `"parentId":"${sourceParentId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;
                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: existingData.items.filter(
                      (ref: Reference) =>
                        !lessons.some((lesson) => readField("id", ref) === lesson.id)
                    ),
                    total: (existingData.total || 0) - newRefs.length,
                  };
                },
              },
            });

            if (sourceParentId !== null) {
              cache.modify({
                id: `Project:${sourceParentId}`,
                fields: {
                  itemCount(existingData = 0) {
                    return existingData - newRefs.length;
                  },
                },
              });
            }
          },
        });
      } catch (error) {
        console.error("Move lessons cache update failed:", error);
      }
    },
  });

  const moveLessons = async (
    lessonIds: string[],
    targetParentId: string | null,
    sourceParentId: string | null
  ) => {
    const result = await moveLessonsMutation({
      variables: {
        input: {
          lessonIds,
          targetParentId,
        },
      },
      context: {
        sourceParentId,
      },
    });

    const maskedLessons = result.data?.moveLesson ?? [];
    const movedLessons = maskedLessons.map((lesson) =>
      useFragment(LessonFieldsFragmentDoc, lesson)
    );

    return movedLessons;
  };

  return { moveLessons, loading };
}
